import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { compressImageFile } from '../utils/compressImage';
import { stashPendingRecognitionPhoto } from '../utils/pendingRecognitionPhoto';

interface QuickAddPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuickAddPhotoModal: React.FC<QuickAddPhotoModalProps> = ({ isOpen, onClose }) => {
  const [capturedPhoto, setCapturedPhoto] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type.startsWith('image/')) {
      setIsProcessing(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          setCapturedPhoto(result);
          setIsProcessing(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContinue = async () => {
    try {
      const blob = await fetch(capturedPhoto).then((r) => r.blob());
      const file = new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' });
      const compressed = await compressImageFile(file);
      stashPendingRecognitionPhoto(compressed);
      navigate('/inventaire', { state: { openCreateForm: true, photo: compressed } });
    } catch {
      stashPendingRecognitionPhoto(capturedPhoto);
      navigate('/inventaire', { state: { openCreateForm: true, photo: capturedPhoto } });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                📸 Ajouter un Objet par Photo
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              {!capturedPhoto ? (
                <>
                  <p className="text-sm text-gray-600">
                    Prenez une photo de l'objet pour une reconnaissance automatique et une création rapide.
                  </p>
                  
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <span className="text-3xl">📷</span>
                      <span className="font-medium text-gray-700">Choisir une photo</span>
                    </button>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                  </div>

                  {isProcessing && (
                    <div className="text-center text-sm text-gray-500">
                      Traitement de la photo...
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="relative">
                    <img 
                      src={capturedPhoto} 
                      alt="Photo capturée" 
                      className="w-full h-64 object-contain bg-gray-100 rounded-lg"
                    />
                    <button
                      onClick={() => setCapturedPhoto('')}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">
                      ✅ Photo capturée ! Vous pourrez compléter les détails de l'objet sur la page suivante.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {capturedPhoto && (
              <button
                onClick={handleContinue}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Continuer
              </button>
            )}
            <button
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {capturedPhoto ? 'Annuler' : 'Fermer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAddPhotoModal;

